import { Component, Input } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';

@Component({
  standalone: false,
  templateUrl: './gains-form-item.component.html',
  styleUrls: ['./gains-form-item.component.scss']
})
export class GainsFormItemComponent {
  @Input() errorMessages: Record<string, string> = {
    required: 'This field is required.',
    email: 'Please enter a valid email address.'
  };
  @Input() label?: string;
  @Input() required = false;
  @Input() error = false;
  @Input() placeholder = '';
  @Input() icon?: string;
  @Input() customFormControl?: FormControl;
  @Input() disabledState = false;
  @Input() showErrorMessage = true;
  /** Shows the errors without waiting for the field to be touched, e.g. on a form just created. */
  @Input() showErrors = false;
  internalFormControl!: FormControl;
  setControl() {
    if (!this.internalFormControl) {
      if (this.customFormControl) {
        this.internalFormControl = this.customFormControl;
      } else {
        this.internalFormControl = new FormControl('');
      }

      if (this.required) {
        this.internalFormControl.addValidators(Validators.required);
        this.internalFormControl.updateValueAndValidity();
      }

      if (this.disabledState) {
        this.internalFormControl.disable();
      }
    }
  }
  checkError() {
    if (this.internalFormControl.touched || this.internalFormControl.dirty) {
      this.error = this.internalFormControl.invalid;
    }
  }

  /** A value from the parent form; subclasses whose control holds another shape override it (gains-select). */
  writeValue(value: any): void {
    if (value !== this.internalFormControl?.value) {
      // A value from the parent is not a change from the view: echoing it back would mark the parent control dirty, and
      // throws while Angular swaps a form, before the new control is attached
      this.internalFormControl?.setValue(value, { emitEvent: false, emitViewToModelChange: false });
    }
  }

  /** The red border: set by the parent, or an invalid value shown up front. */
  get displayError(): boolean {
    return this.error || (this.showErrors && !!this.internalFormControl?.invalid && this.internalFormControl.enabled);
  }

  /** Errors are shown once the field is touched, or straight away with showErrors. */
  private get errorsRevealed(): boolean {
    return this.internalFormControl.touched || (this.showErrors && this.internalFormControl.enabled);
  }

  get errorMessagesToDisplay(): string[] {
    if (!this.internalFormControl?.errors || !this.showErrorMessage || !this.errorsRevealed) return [];
    const errors = this.internalFormControl.errors;
    const keys = Object.keys(errors);

    return keys.map(key => {
      if (this.errorMessages && this.errorMessages[key]) {
        // Use custom message if provided
        return this.errorMessages[key];
      }
      // Bounds explain themselves from what the validator reports
      if (key === 'min') return `Must be ${errors[key].min} or more.`;
      if (key === 'max') return `Must be ${errors[key].max} or less.`;
      // Generic fallback message
      return `Invalid field: ${key}`;
    });
  }
}
